import invariant from 'react-utils/invariant';
import { NavigationReporterOptions } from './BaseReporter';
import CartographerReporter, {
  CartographerEndpoint,
} from './CartographerReporter';
import { ReporterAction, Checks, RouteSpec } from '../types';
import { DEFAULT_TIMEOUT } from '../Constants';
import {
  onVisibilityChange,
  visibilityState,
  wasHiddenOnScriptStart,
  getWasHiddenOnScriptStart,
} from '../visibility';
import { PageLoadMetrics } from '../Metrics';
import { isReload, performanceGetEntriesByType } from '../navigation';
import { getHubHttpData } from '../httpRequestsStats';
import {
  getJSChunkData,
  STATIC_DOMAIN_REGEX,
  CACHE_DURATION_MS,
} from '../jsChunkStats';
import { getIsAiCopilotEnabled } from '../aiCopilot';
import { getRelativeMarkerTimings } from '../markerUtils';
import { captureMessage, capturePageEvent } from '../ravenUtils';
import { cleanseRoute } from '../cleanseRoute';
import { isPrerendering } from '../env';
import { maybeGetReferrerApp } from '../appTransitionHistory';

type FinishStatus = 'success' | 'partial_success' | 'failure';
type FailureType = 'errorSelector' | 'watchdogExpired';

interface FinishAttrs {
  status: FinishStatus;
  route: string;
  duration: number;
  timestamp: number;
  totalTimeFromRouteStart: number;
  pathname?: string;
  scenario?: string;
  failureType?: FailureType;
  selector?: string;
}

export default class ReaganCompatReporter extends CartographerReporter {
  finished: boolean;
  abandonedTimes: number[];
  lastAbandonedTimestamp: number | null;
  wasHidden: boolean;
  isAbandoned: boolean;
  prerendering: boolean | undefined;
  hiddenEntirelyBeforeRhumbFinished: boolean;
  hiddenWithin1000ms: boolean;
  hiddenWithin500ms: boolean;
  finishedTimestamp: number | null;
  finishedStatus: FinishStatus | null;
  reportedMarkers: Set<string>;
  currentRoute: string | null;
  initialLoadCompleted: boolean;
  private _destroyVisibilityListener: () => void;
  constructor(options: NavigationReporterOptions) {
    super(options);
    this.abandonedTimes = [];
    this.lastAbandonedTimestamp = null;
    this.finished = false;
    this.wasHidden =
      getWasHiddenOnScriptStart() || visibilityState() === 'hidden';
    this.isAbandoned = false;
    this.prerendering = isPrerendering();
    this.finishedTimestamp = null;
    this.finishedStatus = null;
    this.reportedMarkers = new Set();
    this.currentRoute = null;
    this.initialLoadCompleted = false;
    // These flags measure if the page was hidden within 500ms (user switched to another tab or window)
    // and remained invisible. This is used to filter out false failures when the page was hidden before
    // the user was frustrated, because the tab becomes throttled and loads very slowly.
    this.hiddenEntirelyBeforeRhumbFinished =
      wasHiddenOnScriptStart && visibilityState() === 'hidden';
    this.hiddenWithin1000ms = visibilityState() === 'hidden';
    this.hiddenWithin500ms = visibilityState() === 'hidden';
    this.setCustomAttribute('currentVisibility', visibilityState());
    this.setCustomAttribute('visibility', visibilityState());

    let becameVisibleAgain = false;

    this._destroyVisibilityListener = onVisibilityChange((state) => {
      this.setCustomAttribute('currentVisibility', state);

      if (this.hiddenEntirelyBeforeRhumbFinished && state === 'visible') {
        this.hiddenEntirelyBeforeRhumbFinished = false;
      }

      if (state === 'visible') {
        if (!this.wasHidden) {
          this.wasHidden = true;
        }
        becameVisibleAgain = true;
        this.hiddenWithin1000ms = false;
        this.hiddenWithin500ms = false;
      }

      if (state === 'hidden' && !this.wasHidden) {
        this.wasHidden = true;
        this.setCustomAttribute('visibility', 'hidden');

        if (!becameVisibleAgain) {
          const now = performance.now();

          if (now <= 1000) {
            this.hiddenWithin1000ms = true;
          }

          if (now <= 500) {
            this.hiddenWithin500ms = true;
          }
        }
      }
    });
  }

  destroy() {
    super.destroy();
    this._destroyVisibilityListener();
  }

  getCacheStatusData() {
    const cacheStatusData: Record<
      string,
      { cached: boolean; duration: number }
    > = {};

    this.performanceEntries().forEach((timing) => {
      if (timing.name.endsWith('.js')) {
        const fileName = timing.name.replace(STATIC_DOMAIN_REGEX, '');
        cacheStatusData[fileName] = {
          cached: timing.duration <= CACHE_DURATION_MS,
          duration: timing.duration,
        };
      }
    });

    return cacheStatusData;
  }

  incrementTimeoutCounters(
    route: string,
    isAiCopilotEnabled: string,
    referrerApp: string | null
  ) {
    const metadata = {
      route: cleanseRoute(route),
      isAiCopilotEnabled,
      isGraniteEnabled: String(this.options.isGraniteEnabled ?? false),
      isGraniteRemoteTransition: String(!!this.softNavigationContext),
      ...(referrerApp ? { referrerApp } : {}),
    };

    if (!this.wasHidden) {
      PageLoadMetrics.counter('timeouts', metadata).increment();
    }

    if (!this.hiddenWithin1000ms) {
      PageLoadMetrics.counter(
        'timeouts_1000ms_visiblity_threshold',
        metadata
      ).increment();
    }

    if (!this.hiddenWithin500ms) {
      PageLoadMetrics.counter(
        'timeouts_500ms_visiblity_threshold',
        metadata
      ).increment();
    }

    if (!this.hiddenEntirelyBeforeRhumbFinished) {
      PageLoadMetrics.counter(
        'timeouts_entirely_hidden_filtered_out',
        metadata
      ).increment();
    }
  }

  reportTimeoutForRaceCondition(
    route: string,
    duration: number,
    timestamp: number,
    totalTimeFromRouteStart: number,
    checks: Checks,
    referrerApp: string | null,
    isAiCopilotEnabled: string,
    extraContext: {
      scenario: string;
      selector?: string;
      pathname?: string;
      detectedInPartialSuccess: boolean;
    }
  ) {
    // Log detection of race condition for monitoring
    capturePageEvent('rhumbTimeoutRaceConditionDetected', {
      extra: {
        duration,
        totalTimeFromRouteStart,
        totalDuration: duration + totalTimeFromRouteStart,
        timestamp,
        route,
        ...extraContext,
      },
    });

    // Increment timeout counters
    this.incrementTimeoutCounters(route, isAiCopilotEnabled, referrerApp);

    this.finish(
      {
        status: 'failure',
        failureType: 'watchdogExpired',
        duration,
        timestamp,
        totalTimeFromRouteStart,
        route,
      },
      checks,
      referrerApp,
      totalTimeFromRouteStart
    );
    this.performanceMark(`mark_all_failure_watchdog_expired`);
    this.finished = true;
  }

  getNumFailedImages() {
    return Array.from(document.getElementsByTagName('img')).reduce(
      (total, ele) => {
        return ele.src && ele.naturalHeight === 0 && ele.naturalWidth === 0
          ? total + 1
          : total;
      },
      0
    );
  }

  finish(
    attrs: FinishAttrs,
    checks: Checks,
    referrerApp: string | null,
    routeStartTimestamp: number
  ) {
    const hubHttpData = getHubHttpData(attrs.timestamp);
    const avgDurationBeforePreviousRhumbAborts = this.abandonedTimes.reduce(
      (acc, duration) => {
        return acc + duration / this.abandonedTimes.length;
      },
      0
    );

    const reaganTiming = getRelativeMarkerTimings(checks, routeStartTimestamp);
    const reaganTimingWithPrefix: Record<string, number> = {};
    Object.keys(reaganTiming).forEach((marker) => {
      reaganTimingWithPrefix[`marker_timing_${marker}`] = reaganTiming[marker];
      this.reportedMarkers.add(marker);
    });

    this.setCustomAttribute(
      'numReaganChecksStarted',
      this.abandonedTimes.length + 1
    );
    this.setCustomAttribute(
      'numPreviousReaganChecksAborted',
      this.abandonedTimes.length
    );
    this.setCustomAttribute(
      'avgDurationBeforePreviousReaganAborts',
      avgDurationBeforePreviousRhumbAborts
    );
    this.setCustomAttribute('numPreviousReaganChecksFailed', 0);
    this.setCustomAttribute('numPreviousReaganChecksSuccessful', 0);

    const activationStartMs =
      // @ts-expect-error PerformanceEntry.activationStart is only available in Chromium
      performanceGetEntriesByType('navigation')?.[0]?.activationStart || 0;

    PageLoadMetrics.counter('rhumb-finished', {
      status: attrs.status,
      isGraniteEnabled: String(this.options.isGraniteEnabled ?? false),
      isGraniteRemoteTransition: String(!!this.softNavigationContext),
    }).increment();

    const softNavCtx = this.softNavigationContext;
    const adjustedDurationMs = this.getAdjustedDurationMs(
      attrs.timestamp,
      attrs.duration + attrs.totalTimeFromRouteStart
    );
    if (adjustedDurationMs < 0) {
      captureMessage(
        `Navigation duration was negative (${adjustedDurationMs / 1000}s)${
          this.softNavigationContext ? ' (granite remote transition)' : ''
        }, skipping Cartographer report`,
        { level: 'warning' }
      );
      return;
    }
    const adjustedDuration = adjustedDurationMs / 1000;

    this.finishedTimestamp = attrs.timestamp;
    this.finishedStatus = attrs.status;
    this.currentRoute = attrs.route;
    this.initialLoadCompleted = true;

    const jsChunkData = getJSChunkData(attrs.timestamp);

    capturePageEvent('rhumbFinished', {
      extra: {
        ...attrs,
        duration: adjustedDuration,
        ...hubHttpData,
        ...jsChunkData,
        numChecksStarted: this.abandonedTimes.length + 1,
        numPreviousChecksAborted: this.abandonedTimes.length,
        avgDurationBeforePreviousRhumbAborts,
        numFailedImages: this.getNumFailedImages(),
        allVisibleMarkers: JSON.stringify(Object.keys(checks)),
        wasPrerendered: this.prerendering || activationStartMs > 0,
        wasHiddenOnScriptStart,
        wasEverHidden: this.wasHidden,
        wasHiddenWhenRhumbFinished: visibilityState() === 'hidden',
        hiddenEntirelyBeforeRhumbFinished:
          this.hiddenEntirelyBeforeRhumbFinished,
        hiddenWithin1000ms: this.hiddenWithin1000ms,
        hiddenWithin500ms: this.hiddenWithin500ms,
        referrerApp,
        isGraniteRemoteTransition: !!softNavCtx,
        isGraniteEnabled: this.options.isGraniteEnabled ?? false,
        ...(softNavCtx ? { fromRemoteApp: softNavCtx.fromRemoteApp } : {}),
        ...reaganTimingWithPrefix,
      },
    });

    const fromRoute = softNavCtx
      ? {
          from: {
            pathname: softNavCtx.fromPathname,
            route: softNavCtx.fromRoute,
          },
        }
      : {};

    this.sendActions(
      [
        {
          to: {
            pathname: attrs.pathname,
            route: attrs.route,
            scenario: attrs.scenario,
          },
          ...fromRoute,
          status: attrs.status,
          wasHidden: this.wasHidden,
          isHidden: visibilityState() === 'hidden',
          duration: adjustedDuration,
          failureType: attrs.failureType,
          isReload: softNavCtx ? false : isReload(),
          ...(softNavCtx ? { isGraniteRemoteTransition: true } : {}),
          isGraniteEnabled: this.options.isGraniteEnabled ?? false,
          timestamp: attrs.timestamp,
          markers: reaganTiming,
          initialUrl: this.initialUrl,
          ...(referrerApp ? { referrerApp } : {}),
          ...(jsChunkData
            ? {
                numJSChunksLoaded: jsChunkData.numJSChunksLoaded,
                numJSChunksCached: jsChunkData.numJSChunksCached,
                numJSChunksUncached: jsChunkData.numJSChunksUncached,
              }
            : {}),
        },
      ],
      CartographerEndpoint.Navigation
    );
  }

  private getNewMarkers(
    checks: Checks,
    routeStartTimestamp: number,
    maxTimeSinceRouteStart = Infinity
  ): Record<string, number> {
    const newMarkers: Record<string, number> = {};

    Object.keys(checks).forEach((marker) => {
      if (checks[marker] && !this.reportedMarkers.has(marker)) {
        const relativeTimestamp =
          checks[marker].timestamp - routeStartTimestamp;
        if (relativeTimestamp <= maxTimeSinceRouteStart) {
          newMarkers[marker] = relativeTimestamp;
          this.reportedMarkers.add(marker);
        }
      }
    });

    return newMarkers;
  }

  private reportLateMarkers(
    markers: Record<string, number>,
    routeSpec: RouteSpec,
    pathname: string | undefined,
    routeStartTimestamp: number
  ) {
    if (!this.finishedStatus) {
      captureMessage(
        '[react-rhumb] finishedStatus must be set when reporting late markers',
        {
          level: 'error',
          extra: {
            route: routeSpec.route,
            pathname,
            markers,
          },
        }
      );
      return;
    }

    this.pushNavigationAction({
      to: {
        route: routeSpec.route,
        pathname,
      },
      status: this.finishedStatus,
      wasHidden: this.wasHidden,
      isHidden: visibilityState() === 'hidden',
      timestamp: routeStartTimestamp + Math.max(...Object.values(markers)),
      markers,
      isAfterRhumbFinished: true,
      initialUrl: this.initialUrl,
    });
    this.flushNavigationQueue();
  }

  report(action: ReporterAction) {
    if (action.type === 'COMPONENT_RENDERED' || !this) {
      return;
    }

    // After route finishes, only process CHECKS_CHANGED to detect late markers or ROUTE_STARTED to begin new navigation
    if (
      this.finished &&
      action.type !== 'CHECKS_CHANGED' &&
      action.type !== 'ROUTE_STARTED'
    ) {
      return;
    }

    const referrerApp = maybeGetReferrerApp(this.softNavigationContext);

    const {
      entry: {
        timestamp: totalTimeFromRouteStart = 0,
        checks,
        expiredTimestamp,
        pathname,
      },
      routeSpec,
    } = action.payload;

    switch (action.type) {
      case 'ROUTE_SUCCEEDED':
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_FAILED':
      case 'ROUTE_TIMEOUT_EXPIRED':
      case 'ROUTE_UNEXPECTED': {
        this.finished = true;
        break;
      }
      default:
    }

    const isAiCopilotEnabled = String(getIsAiCopilotEnabled());

    switch (action.type) {
      case 'ROUTE_STARTED': {
        if (this.lastAbandonedTimestamp) {
          this.abandonedTimes.push(
            totalTimeFromRouteStart - this.lastAbandonedTimestamp
          );
        }
        this.isAbandoned = false;
        if (!this.initialLoadCompleted) {
          this.finished = false;
        } else {
          this.abandonedTimes = [];
          this.lastAbandonedTimestamp = null;
        }
        this.finishedTimestamp = null;
        this.finishedStatus = null;
        this.reportedMarkers.clear();
        this.currentRoute = routeSpec.route;

        break;
      }
      case 'ROUTE_ABANDONED': {
        this.lastAbandonedTimestamp = totalTimeFromRouteStart;
        this.isAbandoned = true;
        break;
      }
      case 'ROUTE_PARTIAL_SUCCESS': {
        const { partialSuccess, route } = routeSpec;
        const {
          extra: { scenario },
        } = action.payload;
        const markers = partialSuccess[scenario] || [];
        const { duration, timestamp, selector } = this.toTimings(
          checks,
          totalTimeFromRouteStart,
          markers
        );

        // Validate that success duration is under timeout threshold
        // This catches race conditions where success markers appear after timeout
        // threshold but before the delayed timeout callback fires (e.g., under high CPU load)
        if (
          duration +
            totalTimeFromRouteStart -
            (this.softNavigationContext?.timestamp || 0) >
          DEFAULT_TIMEOUT
        ) {
          this.reportTimeoutForRaceCondition(
            route,
            duration,
            timestamp,
            totalTimeFromRouteStart,
            checks,
            referrerApp,
            isAiCopilotEnabled,
            {
              scenario,
              selector,
              pathname,
              detectedInPartialSuccess: true,
            }
          );
          break;
        }

        if (!this.wasHidden) {
          PageLoadMetrics.timer('partial_success', {
            scenario,
            route: cleanseRoute(route),
            isAiCopilotEnabled,
            isGraniteEnabled: String(this.options.isGraniteEnabled ?? false),
            isGraniteRemoteTransition: String(!!this.softNavigationContext),
            ...(referrerApp ? { referrerApp } : {}),
          }).update(
            this.getAdjustedDurationMs(
              timestamp,
              totalTimeFromRouteStart + duration
            )
          );
        }
        this.finish(
          {
            status: 'partial_success',
            duration,
            timestamp,
            totalTimeFromRouteStart,
            pathname,
            scenario,
            selector,
            route,
          },
          checks,
          referrerApp,
          totalTimeFromRouteStart
        );
        this.performanceMark(`mark_all_success`);
        break;
      }
      case 'ROUTE_SUCCEEDED': {
        const { success, route } = routeSpec;
        const {
          extra: { scenario },
        } = action.payload;
        if (process.env.NODE_ENV !== 'production') {
          invariant(
            success[scenario].length > 0,
            'routeSpec for %s must have at least one `success` marker for %s',
            route,
            scenario
          );
        }
        const markers = success[scenario] || [];
        const { duration, timestamp, selector } = this.toTimings(
          checks,
          totalTimeFromRouteStart,
          markers
        );

        // Validate that success duration is under timeout threshold
        // This catches race conditions where success markers appear after timeout
        // threshold but before the delayed timeout callback fires (e.g., under high CPU load)
        if (
          duration +
            totalTimeFromRouteStart -
            (this.softNavigationContext?.timestamp || 0) >
          DEFAULT_TIMEOUT
        ) {
          this.reportTimeoutForRaceCondition(
            route,
            duration,
            timestamp,
            totalTimeFromRouteStart,
            checks,
            referrerApp,
            isAiCopilotEnabled,
            {
              scenario,
              selector,
              pathname,
              detectedInPartialSuccess: false,
            }
          );
          break;
        }

        if (!this.wasHidden) {
          PageLoadMetrics.timer('succeeded', {
            scenario,
            route: cleanseRoute(route),
            isAiCopilotEnabled,
            isGraniteEnabled: String(this.options.isGraniteEnabled ?? false),
            isGraniteRemoteTransition: String(!!this.softNavigationContext),
            ...(referrerApp ? { referrerApp } : {}),
          }).update(
            this.getAdjustedDurationMs(
              timestamp,
              totalTimeFromRouteStart + duration
            )
          );
        }
        const activationStartMs =
          // @ts-expect-error PerformanceEntry.activationStart is only available in Chromium
          performanceGetEntriesByType('navigation')?.[0]?.activationStart || 0;
        if (activationStartMs > 0) {
          PageLoadMetrics.timer('succeeded-prerender', {
            scenario,
            route: cleanseRoute(route),
            isAiCopilotEnabled,
            isGraniteEnabled: String(this.options.isGraniteEnabled ?? false),
            isGraniteRemoteTransition: String(!!this.softNavigationContext),
            ...(referrerApp ? { referrerApp } : {}),
          }).update(totalTimeFromRouteStart + duration - activationStartMs);
        }
        this.finish(
          {
            status: 'success',
            duration,
            timestamp,
            totalTimeFromRouteStart,
            pathname,
            scenario,
            selector,
            route,
          },
          checks,
          referrerApp,
          totalTimeFromRouteStart
        );
        this.performanceMark(`mark_all_success`);
        break;
      }
      case 'ROUTE_FAILED': {
        // Avoid reporting 'ROUTE_FAILED' after 'ROUTE_ABANDONED' in case there is a race condition where failure markers
        // are still visible in the DOM after navigation away has started. E.g., hub-http can automatically redirect to
        // the login screen on 401s, and this should not be counted as a failure state even if the app briefly shows an error
        // before the redirect completes.
        if (this.isAbandoned) {
          break;
        }
        const { route, error } = routeSpec;
        const markers = error.filter((marker) => checks[marker]);
        if (process.env.NODE_ENV !== 'production') {
          invariant(
            markers.length > 0,
            'routeSpec for %s must have at least one `failure` marker for',
            route
          );
        }
        const { duration, timestamp, selector } = this.toTimings(
          checks,
          totalTimeFromRouteStart,
          markers
        );
        const failedMarker = markers.length ? markers[0] : '';
        if (!this.wasHidden) {
          PageLoadMetrics.counter('failed', {
            route: cleanseRoute(route),
            selector: failedMarker,
            isAiCopilotEnabled,
            isGraniteEnabled: String(this.options.isGraniteEnabled ?? false),
            isGraniteRemoteTransition: String(!!this.softNavigationContext),
            ...(referrerApp ? { referrerApp } : {}),
          }).increment();
        }
        this.finish(
          {
            status: 'failure',
            failureType: 'errorSelector',
            selector,
            duration,
            timestamp,
            totalTimeFromRouteStart,
            route,
            pathname,
          },
          checks,
          referrerApp,
          totalTimeFromRouteStart
        );
        this.performanceMark(`mark_all_failure`);
        break;
      }
      case 'ROUTE_TIMEOUT_EXPIRED': {
        const { route } = routeSpec;

        this.incrementTimeoutCounters(route, isAiCopilotEnabled, referrerApp);

        const timeoutDuration = Math.max(
          0,
          (expiredTimestamp || 0) - totalTimeFromRouteStart
        );

        this.finish(
          {
            status: 'failure',
            failureType: 'watchdogExpired',
            duration: timeoutDuration,
            timestamp: expiredTimestamp || performance.now(),
            totalTimeFromRouteStart,
            route,
          },
          checks,
          referrerApp,
          totalTimeFromRouteStart
        );
        this.performanceMark(`mark_all_failure_watchdog_expired`);
        break;
      }
      case 'CHECKS_CHANGED': {
        // Only report late markers for the same route that finished and within 60s of route start
        if (
          this.finished &&
          this.finishedTimestamp &&
          routeSpec.route === this.currentRoute
        ) {
          const newMarkers = this.getNewMarkers(
            checks,
            totalTimeFromRouteStart,
            60000
          );
          if (Object.keys(newMarkers).length > 0) {
            this.reportLateMarkers(
              newMarkers,
              routeSpec,
              pathname,
              totalTimeFromRouteStart
            );
          }
        }
        break;
      }
      default:
    }
  }
}
